import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  /** Optional custom fallback UI. Receives the caught error for display. */
  fallback?: (error: Error) => ReactNode;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary
 *
 * Catches unhandled JavaScript runtime errors in any child component tree.
 * Prevents a single component crash from taking down the entire application.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 *   // With a custom fallback:
 *   <ErrorBoundary fallback={(err) => <MyFallback message={err.message} />}>
 *     <SomeComponent />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Caught an unhandled error:', error);
    console.error('[ErrorBoundary] Component stack:', info.componentStack);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Render the caller-supplied fallback if provided.
      if (this.props.fallback) {
        return this.props.fallback(this.state.error);
      }

      // Default fallback UI.
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <h2 style={styles.heading}>Something went wrong</h2>
            <p style={styles.body}>
              An unexpected error occurred. Please reload the page. If the
              problem persists, contact support.
            </p>
            <button style={styles.button} onClick={this.handleReload}>
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: 'sans-serif',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
    padding: '40px 48px',
    maxWidth: '480px',
    textAlign: 'center',
  },
  heading: {
    fontSize: '22px',
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '12px',
  },
  body: {
    fontSize: '15px',
    color: '#555555',
    lineHeight: 1.6,
    marginBottom: '24px',
  },
  button: {
    backgroundColor: '#0077cc',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 24px',
    fontSize: '15px',
    cursor: 'pointer',
  },
};

export default ErrorBoundary;
